import React, { useState, useEffect, useContext, Fragment, useRef, useCallback } from 'react';
import { getOccupants } from '../services/occupantService';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate, formatDateForInput, getCurrentDateFormat, getExampleDate } from '../utils/dateUtils';
import { buildApiUrl } from '../config/environment';
import BondModal from './BondModal';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';

const RentalAgreementModal = ({ show, onClose, selectedListing, onSave }) => {
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);
  const currencySymbol = getCurrencySymbol(agencyCurrency);

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  // Helper functions for date format hints
  const getExpectedDateFormat = () => {
    return getCurrentDateFormat(user?.dateFormatPreference, null, agencyCountry);
  };

  const getDateExample = () => {
    return getExampleDate(user?.dateFormatPreference, null, agencyCountry);
  };


  // Changed from editingAgreement to editingTenancy since we're now working with tenancy data
  const [editingTenancy, setEditingTenancy] = useState({});
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [occupants, setOccupants] = useState([]);
  const [occupantSearch, setOccupantSearch] = useState('');
  const [filteredOccupants, setFilteredOccupants] = useState([]);
  const [showOccupantDropdown, setShowOccupantDropdown] = useState(false);
  const [selectedOccupants, setSelectedOccupants] = useState([]);
  const [bondBalance, setBondBalance] = useState(0);
  const [loadingBondBalance, setLoadingBondBalance] = useState(false);

  // New state for edit mode and change tracking - renamed for tenancy
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalTenancy, setOriginalTenancy] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingTenancySelection, setPendingTenancySelection] = useState(null);

  // New state for actions dropdown
  const [selectedAction, setSelectedAction] = useState('');
  const [availableActions, setAvailableActions] = useState([]);

  // New state for managing list of tenancies
  const [tenanciesList, setTenanciesList] = useState([]);

  // New state for BondModal
  const [showBondModal, setShowBondModal] = useState(false);
  const [bondAgreement, setBondAgreement] = useState(null);
  const [bondOccupant, setBondOccupant] = useState(null);

  // State for rental documents
  const [rentalDocuments, setRentalDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const documentInputRef = useRef(null);

  // Status transition mapping - same as before but now for tenancy status
  const statusTransitions = {
    'available': {
      'pending': 'Mark as Pending',
      'active': 'Activate Tenancy',
      'on_hold': 'Put on Hold'
    },
    'pending': {
      'active': 'Activate Tenancy',
      'available': 'Make Available',
      'terminated': 'Terminate Tenancy'
    },
    'active': {
      'expired': 'Mark as Expired',
      'terminated': 'Terminate Tenancy'
    },
    'expired': {
      'active': 'Reactivate Tenancy',
      'terminated': 'Terminate Tenancy',
      'available': 'Make Available'
    },
    'terminated': {
      'available': 'Make Available'
    },
    'on_hold': {
      'available': 'Make Available'
    }
  };

  useEffect(() => {
    if (show) {
      console.log('[RentalAgreementModal] selectedListing on open:', selectedListing);
      loadOccupants();
    }
  }, [show]);

  useEffect(() => {
    // Only reset state when modal first opens or when selectedListing changes
    if (show && selectedListing?.id) {
      console.log('[RentalAgreementModal] Modal opened, selectedListing:', selectedListing);

      // Reset state when modal opens
      setIsCreatingNew(false);
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      setSelectedOccupants([]);
      setOccupantSearch('');

      // Fetch current tenancy from API instead of using selectedListing.current_tenancy
      const loadTenancyData = async () => {
        try {
          const tenancyData = await fetchCurrentTenancy(selectedListing.id);

          if (tenancyData) {
            console.log('[RentalAgreementModal] Fetched tenancy data:', tenancyData);
            setTenanciesList([tenancyData]);
            handleSelectExistingTenancy(tenancyData);
          } else {
            console.log('[RentalAgreementModal] No tenancy found for listing');
            setTenanciesList([]);
            setEditingTenancy({});
          }
        } catch (error) {
          console.error('[RentalAgreementModal] Error loading tenancy data:', error);
          // Fallback to empty state if API call fails
          setTenanciesList([]);
          setEditingTenancy({});
        }
      };

      loadTenancyData();
    } else if (!show) {
      // Reset all state when modal closes
      setEditingTenancy({});
      setIsCreatingNew(false);
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      setSelectedOccupants([]);
      setOccupantSearch('');
      setTenanciesList([]);
    }
  }, [show, selectedListing?.id]);

  useEffect(() => {
    if (occupantSearch) {
      const filtered = occupants.filter(occupant => {
        const fullName = `${occupant.person?.given || occupant.given || occupant.first_name || ''} ${occupant.person?.surname || occupant.surname || occupant.last_name || ''}`.toLowerCase();
        const email = (occupant.person?.email || occupant.email || '').toLowerCase();
        const searchTerm = occupantSearch.toLowerCase();
        return fullName.includes(searchTerm) || email.includes(searchTerm);
      });
      setFilteredOccupants(filtered);
    } else {
      setFilteredOccupants([]);
    }
  }, [occupantSearch, occupants]);

  // Fetch bond balance when editing an existing tenancy
  useEffect(() => {
    if (editingTenancy.id && !isCreatingNew) {
      fetchBondBalance();
    } else {
      setBondBalance(0);
    }
  }, [editingTenancy.id, isCreatingNew]);

  // Detect unsaved changes in edit mode
  useEffect(() => {
    if (isEditMode && Object.keys(originalTenancy).length > 0) {
      // Check if any field except ID and status has changed
      const hasChanges = Object.keys(originalTenancy).some(key => {
        if (key === 'id' || key === 'status') return false;
        return originalTenancy[key] !== editingTenancy[key];
      });
      setHasUnsavedChanges(hasChanges);
    }
  }, [editingTenancy, isEditMode, originalTenancy]);

  // Handle pending tenancy selection after save
  useEffect(() => {
    if (pendingTenancySelection && !hasUnsavedChanges && !isEditMode) {
      handleSelectExistingTenancy(pendingTenancySelection);
      setPendingTenancySelection(null);
    }
  }, [hasUnsavedChanges, isEditMode, pendingTenancySelection]);

  const loadOccupants = async () => {
    try {
      const agencyId = sessionStorage.getItem('agencyId');
      if (!agencyId) {
        throw new Error('Agency ID not found in session storage');
      }

      const occupantData = await getOccupants(agencyId);
      setOccupants(occupantData);
    } catch (error) {
      console.error('Error loading occupants:', error);
    }
  };

  const fetchCurrentTenancy = async (rentalListingId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const url = buildApiUrl(`/rental-listings/${rentalListingId}/tenancy`);
      console.log('[RentalAgreementModal] Fetching tenancy from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 404) {
        // No tenancy found for this listing, which is valid
        console.log('[RentalAgreementModal] No tenancy found for listing:', rentalListingId);
        return null;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch tenancy' }));
        throw new Error(error.message || 'Failed to fetch tenancy');
      }

      const tenancyData = await response.json();
      console.log('[RentalAgreementModal] Fetched tenancy data:', tenancyData);
      return tenancyData;
    } catch (error) {
      console.error('Error fetching current tenancy:', error);
      // Don't throw the error, just return null so the component can still work
      return null;
    }
  };

  const fetchBondBalance = async () => {
    if (!editingTenancy.id || isCreatingNew) {
      setBondBalance(0);
      return;
    }

    setLoadingBondBalance(true);

    try {
      // Check if bond_ledger exists in the tenancy data
      if (editingTenancy.bond_ledger && editingTenancy.bond_ledger.balance) {
        // Use the balance from bond_ledger.balance.amount and convert from cents to dollars
        const balanceAmount = editingTenancy.bond_ledger.balance.amount || 0;
        const balanceInDollars = typeof balanceAmount === 'number' ? (balanceAmount / 100) : 0;
        setBondBalance(balanceInDollars);
        console.log('[RentalAgreementModal] Bond balance from tenancy bond_ledger:', {
          rawAmount: balanceAmount,
          balanceInDollars: balanceInDollars,
          formatted: editingTenancy.bond_ledger.balance.formatted
        });
      } else {
        // No bond_ledger exists, set balance to 0
        setBondBalance(0);
        console.log('[RentalAgreementModal] No bond_ledger found, setting balance to 0');
      }
    } catch (error) {
      console.error('Error processing bond balance:', error);
      setBondBalance(0);
    } finally {
      setLoadingBondBalance(false);
    }
  };

  // Fetch rental documents for the current rental agreement
  const fetchRentalDocuments = useCallback(async (rentalAgreementId) => {
    if (!rentalAgreementId) {
      setRentalDocuments([]);
      return;
    }

    setLoadingDocuments(true);
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(buildApiUrl(`/rental-agreements/${rentalAgreementId}/documents`), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const documents = await response.json();
      setRentalDocuments(Array.isArray(documents) ? documents : []);
    } catch (error) {
      console.error('Error fetching rental documents:', error);
      setRentalDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, []);

  // Upload a rental document
  const handleDocumentUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const rentalAgreementId = editingTenancy.rental_agreement?.id;
    if (!rentalAgreementId) {
      alert('Please save the rental agreement first before uploading documents.');
      return;
    }

    setUploadingDocument(true);
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(buildApiUrl(`/rental-agreements/${rentalAgreementId}/documents`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: 'Failed to upload document' }));
          throw new Error(error.message || `Failed to upload ${file.name}`);
        }
      }

      // Refresh documents list
      await fetchRentalDocuments(rentalAgreementId);
      setShowDocumentUpload(false);

      // Reset file input
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(`Error uploading document: ${error.message}`);
    } finally {
      setUploadingDocument(false);
    }
  };

  // Delete a rental document
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    const rentalAgreementId = editingTenancy.rental_agreement?.id;
    if (!rentalAgreementId) return;

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(buildApiUrl(`/rental-agreements/${rentalAgreementId}/documents/${documentId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      // Refresh documents list
      await fetchRentalDocuments(rentalAgreementId);
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(`Error deleting document: ${error.message}`);
    }
  };

  // Download a rental document
  const handleDownloadDocument = async (doc) => {
    const rentalAgreementId = editingTenancy.rental_agreement?.id;
    if (!rentalAgreementId) return;

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(
        buildApiUrl(`/rental-agreements/${rentalAgreementId}/documents/${doc.id}/download`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      // Stream the response blob and trigger a browser download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert(`Error downloading document: ${error.message}`);
    }
  };

  // Fetch documents when rental agreement changes
  useEffect(() => {
    if (editingTenancy.rental_agreement?.id && !isCreatingNew && !editingTenancy.isNew) {
      fetchRentalDocuments(editingTenancy.rental_agreement.id);
    } else {
      setRentalDocuments([]);
    }
  }, [editingTenancy.rental_agreement?.id, isCreatingNew, editingTenancy.isNew, fetchRentalDocuments]);

  const createBondLedger = async (tenancyId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const url = buildApiUrl(`/tenancies/${tenancyId}/bond-ledger`);
      console.log('[RentalAgreementModal] Creating bond ledger at:', url);

      // Use agencyCurrency from component scope
      const currency = agencyCurrency || 'AUD';

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currency: currency,
          initial_balance: 0
        })
      });

      if (response.status === 409) {
        // Bond ledger already exists, this is fine
        console.log('[RentalAgreementModal] Bond ledger already exists');
        return true;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to create bond ledger' }));
        throw new Error(error.message || 'Failed to create bond ledger');
      }

      const bondLedgerData = await response.json();
      console.log('[RentalAgreementModal] Bond ledger created:', bondLedgerData);

      // Update the editing tenancy with the new bond ledger
      setEditingTenancy(prev => ({
        ...prev,
        bond_ledger: bondLedgerData
      }));

      // Update the bond balance
      setBondBalance(bondLedgerData.balance || 0);

      return true;
    } catch (error) {
      console.error('Error creating bond ledger:', error);
      alert(`Error creating bond ledger: ${error.message}`);
      return false;
    }
  };

  const handleManageBond = async () => {
    console.log('[RentalAgreementModal] editingTenancy object:', editingTenancy);
    console.log('[RentalAgreementModal] editingTenancy.bond_ledger:', editingTenancy.bond_ledger);

    if (!editingTenancy.id || !selectedListing?.id) {
      alert('No tenancy or listing selected');
      return;
    }

    try {

      // Check if bond ledger exists
      let currentTenancy = editingTenancy;
      if (!editingTenancy.bond_ledger_id) {
        console.log('[RentalAgreementModal] No bond ledger found, creating one...');
        const created = await createBondLedger(editingTenancy.id);
        if (!created) {
          return; // Error already shown in createBondLedger
        }

        // Refresh tenancy data to get the newly created bond ledger
        const updatedTenancy = await fetchCurrentTenancy(selectedListing.id);
        if (updatedTenancy) {
          currentTenancy = updatedTenancy;
          setEditingTenancy(updatedTenancy);
          setTenanciesList(prev => prev.map(t => t.id === updatedTenancy.id ? updatedTenancy : t));
        }
      }

      // Open the bond modal with the tenancy and selected occupant
      if (currentTenancy && selectedOccupants.length > 0) {
        setBondAgreement(currentTenancy);
        // Use the first occupant for bond management
        setBondOccupant(selectedOccupants[0]);
        setShowBondModal(true);
      } else if (currentTenancy && selectedOccupants.length === 0) {
        alert('Please select at least one occupant to manage bond');
      }
    } catch (error) {
      console.error('Error managing bond:', error);
      alert(`Error managing bond: ${error.message}`);
    }
  };

  const handleBondSave = (tenancy, occupant) => {
    setBondAgreement(tenancy);
    setBondOccupant(occupant);
    setShowBondModal(false);
    // Refresh bond balance after bond operations
    fetchBondBalance();
  };

  // Check if new tenancy can be created - Updated to check current tenancy status
  const canCreateNewTenancy = () => {
    // Check if there's already a draft tenancy in the list
    const hasDraftTenancy = tenanciesList.some(tenancy => tenancy.isNew);
    if (hasDraftTenancy) {
      return { canCreate: false, reason: 'A draft tenancy already exists. Please complete or remove it first.' };
    }

    // Check if current tenancy allows creation of new tenancy
    const currentTenancy = tenanciesList.find(t => !t.isNew);
    if (currentTenancy) {
      const status = currentTenancy.status?.toLowerCase();
      if (status !== 'expired' && status !== 'terminated') {
        return {
          canCreate: false,
          reason: `Cannot create new tenancy while current tenancy is "${status}". Current tenancy must be "Expired" or "Terminated".`
        };
      }
    }

    return { canCreate: true, reason: '' };
  };

  // Check if tenancy can be commenced (is new status)
  const canCommenceTenancy = (tenancy) => {
    return tenancy.status?.toLowerCase() === 'pending' || tenancy.status === 'pending';
  };

  const handleCommenceTenancy = (tenancy) => {
    // Set the tenancy status to active
    const updatedTenancy = {
      ...tenancy,
      status: 'active'
    };

    // Call onSave to update the parent component
    onSave(updatedTenancy);
  };

  const handleNewTenancy = () => {
    const createCheck = canCreateNewTenancy();
    if (!createCheck.canCreate) {
      alert(createCheck.reason);
      return;
    }

    setIsCreatingNew(true);
    setIsEditMode(true); // New tenancies should be editable by default
    setSelectedOccupants([]);
    setOccupantSearch('');

    // Create a new draft tenancy
    const newTenancy = {
      id: `new-${Date.now()}`, // Temporary ID for new tenancy
      rental_listing_id: selectedListing?.id,
      status: 'pending',
      rental_agreement: {
        id: null,
        start_date: '',
        end_date: '',
        amount: selectedListing?.rent_amount?.amount || 0,
        rent_frequency: 'monthly',
        payment_type: 'prepay',
        bond: 0, // always a number
        renter_ids: []
      },
      isNew: true // Flag to identify this is a new draft tenancy
    };

    setEditingTenancy(newTenancy);

    // Add the new tenancy to the list temporarily for display
    setTenanciesList(prev => [newTenancy, ...prev]);
  };

  // Edit mode functionality
  const handleEditToggle = () => {
    if (isEditMode) {
      // Currently in edit mode, check if we should save or just exit
      if (hasUnsavedChanges) {
        const confirmed = window.confirm('Do you want to save your changes?');
        if (confirmed) {
          handleSaveChanges();
        } else {
          // Revert changes
          setEditingTenancy({ ...originalTenancy });
          setIsEditMode(false);
          setHasUnsavedChanges(false);
        }
      } else {
        // No changes, just exit edit mode
        setIsEditMode(false);
      }
    } else {
      // Enter edit mode
      setIsEditMode(true);
      setOriginalTenancy({ ...editingTenancy });
      setHasUnsavedChanges(false);
    }
  };

  const handleSaveChanges = async () => {
    const confirmed = window.confirm('Are you sure you want to save these changes?');
    if (confirmed) {
      try {
        await handleSave();
        setIsEditMode(false);
        setHasUnsavedChanges(false);
        setOriginalTenancy({});
      } catch (error) {
        console.error('Error saving changes:', error);
      }
    }
  };

  const handleTenancySelection = (tenancy) => {
    if (isEditMode && hasUnsavedChanges) {
      // Prompt to save changes
      const confirmed = window.confirm('You have unsaved changes. Do you want to save them before switching tenancies?');
      if (confirmed) {
        // Save current changes first, then switch
        setPendingTenancySelection(tenancy);
        handleSaveChanges();
      } else {
        // Discard changes and switch
        setIsEditMode(false);
        setHasUnsavedChanges(false);
        setOriginalTenancy({});
        handleSelectExistingTenancy(tenancy);
      }
    } else {
      // No unsaved changes, switch directly
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      setOriginalTenancy({});
      handleSelectExistingTenancy(tenancy);
    }
  };

  const handleSelectExistingTenancy = (tenancy) => {
    // Keep creating new state for draft tenancies
    setIsCreatingNew(tenancy.isNew || false);

    // Get rental agreement from tenancy
    const agreement = tenancy.rental_agreement;

    // Convert amount from cents to dollars for display if needed
    let displayAmount = 0;
    if (agreement?.amount?.amount) {
      // New API format: amount is an object with amount in cents
      displayAmount = agreement.amount.amount / 100;
    } else if (typeof agreement?.amount === 'number') {
      // Fallback: direct number format (assume already in dollars)
      displayAmount = agreement.amount;
    }

    // Normalize bond to always be a number - handle new API format
    let bondValue = 0;
    let bondFormatted = '';
    if (agreement?.bond) {
      if (typeof agreement.bond === 'object' && agreement.bond.amount !== undefined) {
        // New API format: { amount: 0, currency: "", formatted: "0" }
        // Convert from cents to dollars for editing
        bondValue = (agreement.bond.amount || 0) / 100;
        bondFormatted = agreement.bond.formatted || '';
      } else if (typeof agreement.bond === 'number') {
        // Direct number format (assume already in dollars)
        bondValue = agreement.bond;
        bondFormatted = `${getCurrencySymbol(agencyCurrency)}${bondValue.toFixed(2)}`;
      }
    }

    console.log('[RentalAgreementModal] Bond processing:', {
      originalBond: agreement?.bond,
      extractedBondValue: bondValue,
      bondFormatted: bondFormatted,
      bondType: typeof agreement?.bond
    });

    const normalizedTenancy = {
      ...tenancy,
      id: tenancy.id,
      status: tenancy.status,
      rental_agreement: {
        ...agreement,
        id: agreement?.id,
        start_date: agreement?.start_date || '',
        end_date: agreement?.end_date || '',
        amount: displayAmount,
        rent_frequency: agreement?.rent_frequency || 'monthly',
        payment_type: agreement?.payment_type || 'prepay',
        bond: bondValue, // always a number for editing
        bond_formatted: bondFormatted // formatted string for display
      },
      original_bond_formatted: bondFormatted // preserve original formatted value
    };

    setEditingTenancy(normalizedTenancy);

    // Set occupant search if agreement has renters - now handling multiple
    const renters = agreement?.renters || [];
    if (renters && renters.length > 0) {
      setSelectedOccupants(renters);
      // Show count in search field
      setOccupantSearch(`${renters.length} occupant${renters.length > 1 ? 's' : ''} selected`);
    } else {
      setSelectedOccupants([]);
      setOccupantSearch('');
    }

    // Draft tenancies should be editable by default
    setIsEditMode(tenancy.isNew || false);
    setHasUnsavedChanges(false);
    setOriginalTenancy({});
  };

  const handleOccupantSelect = (occupant) => {
    // Check if occupant is already selected
    if (selectedOccupants.find(o => o.id === occupant.id)) {
      alert('This occupant is already selected');
      return;
    }

    const updatedOccupants = [...selectedOccupants, occupant];
    setSelectedOccupants(updatedOccupants);
    setOccupantSearch(''); // Clear search after selection
    setEditingTenancy({
      ...editingTenancy,
      rental_agreement: {
        ...editingTenancy.rental_agreement,
        renter_ids: updatedOccupants.map(o => o.id)
      }
    });
    setShowOccupantDropdown(false);
  };

  const handleRemoveOccupant = (occupantId) => {
    const updatedOccupants = selectedOccupants.filter(o => o.id !== occupantId);
    setSelectedOccupants(updatedOccupants);
    setEditingTenancy({
      ...editingTenancy,
      rental_agreement: {
        ...editingTenancy.rental_agreement,
        renter_ids: updatedOccupants.map(o => o.id)
      }
    });
  };

  const handleOccupantSearchChange = (e) => {
    setOccupantSearch(e.target.value);
    setShowOccupantDropdown(true);
  };

  const handleSave = async () => {
    try {
      const rentalListingId = selectedListing?.id;

      if (!rentalListingId) {
        throw new Error('Rental Listing ID not found');
      }

      // Validate that at least 1 occupant is selected for new tenancies
      if (isCreatingNew && selectedOccupants.length === 0) {
        alert('Please select at least 1 occupant before creating the tenancy.');
        return;
      }

      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Prepare the request payload for rental agreement creation/update
      // Convert amounts from dollars to cents (multiply by 100) for the new API
      const amountInCents = Math.round((editingTenancy.rental_agreement?.amount || 0) * 100);
      const bondInCents = Math.round((editingTenancy.rental_agreement?.bond || 0) * 100);

      const payload = {
        amount: amountInCents, // Now an integer in cents
        start_date: editingTenancy.rental_agreement?.start_date || '',
        end_date: editingTenancy.rental_agreement?.end_date || '',
        rent_frequency: editingTenancy.rental_agreement?.rent_frequency || 'monthly',
        payment_type: editingTenancy.rental_agreement?.payment_type || 'prepay',
        rent_due_day: 1, // Default to 1st of month
        auto_rent_enabled: false, // Default value
        renter_ids: selectedOccupants.map(o => o.id),
        bond: bondInCents // Now an integer in cents
      };

      let response;
      let url;

      if (isCreatingNew) {
        // POST: Create new rental agreement (which creates tenancy automatically)
        url = buildApiUrl(`/rental-listings/${rentalListingId}/rental-agreements`);
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        // PATCH: Update existing rental agreement
        url = buildApiUrl(`/rental-listings/${rentalListingId}/rental-agreements/${editingTenancy.rental_agreement?.id}`);
        response = await fetch(url, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to save rental agreement' }));
        throw new Error(error.message || `Failed to ${isCreatingNew ? 'create' : 'update'} rental agreement`);
      }

      const savedData = await response.json();

      // Update the tenancies list with the saved data
      if (isCreatingNew) {
        // Remove the draft tenancy and add the real one
        setTenanciesList(prev => prev.filter(t => !t.isNew).concat([savedData]));

        // Call onSave callback with the saved data to refresh parent
        onSave(savedData);

        // Close the modal after creating new tenancy
        onClose();
      } else {
        // Update the existing tenancy in the list
        setTenanciesList(prev => prev.map(t => t.id === editingTenancy.id ? savedData : t));

        // Update the editing tenancy with saved data
        setEditingTenancy(savedData);

        // Call onSave callback with the saved data
        onSave(savedData);
        onClose();
      }

    } catch (error) {
      console.error('Error saving rental agreement:', error);
      alert(`Error ${isCreatingNew ? 'creating' : 'updating'} rental agreement: ${error.message}`);
    }
  };

  const handleCancel = () => {
    if (isCreatingNew) {
      // Remove the draft tenancy from the list
      setTenanciesList(prev => prev.filter(t => !t.isNew));
    }
    setIsCreatingNew(false);
    setEditingTenancy({});
    setSelectedOccupants([]);
    setOccupantSearch('');
    onClose();
  };

  // Function to handle tenancy status actions
  const handleActionExecute = async () => {
    if (!selectedAction || !editingTenancy.id) return;

    const actionLabel = availableActions.find(a => a.value === selectedAction)?.label;
    const confirmed = window.confirm(`Are you sure you want to ${actionLabel.toLowerCase()}?`);

    if (!confirmed) return;

    try {
      const token = sessionStorage.getItem('access_token');

      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Get tenancy ID from the editing tenancy
      const tenancyId = editingTenancy.id;

      if (!tenancyId) {
        throw new Error('Tenancy ID not found');
      }

      // Call API to update tenancy status using the correct endpoint
      const url = buildApiUrl(`/tenancies/${tenancyId}/status`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: selectedAction
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update tenancy status' }));
        throw new Error(error.message || 'Failed to update tenancy status');
      }

      const updatedTenancy = await response.json();

      // Update local state with the new status without closing the modal
      const refreshedTenancy = { ...editingTenancy, ...updatedTenancy, status: selectedAction };
      setEditingTenancy(refreshedTenancy);
      setTenanciesList(prev => prev.map(t => t.id === refreshedTenancy.id ? refreshedTenancy : t));

      // Reset action selection
      setSelectedAction('');

      alert(`Tenancy status updated to ${actionLabel.toLowerCase()}`);

    } catch (error) {
      alert(`Error updating tenancy status: ${error.message}`);
    }
  };

  // Update available actions when tenancy changes
  useEffect(() => {
    if (editingTenancy.id && editingTenancy.status) {
      const currentStatus = editingTenancy.status?.toLowerCase() || editingTenancy.status;
      const transitions = statusTransitions[currentStatus] || {};
      const actions = Object.entries(transitions).map(([status, label]) => ({
        value: status,
        label: label
      }));
      setAvailableActions(actions);
      setSelectedAction(''); // Reset selection
    } else {
      setAvailableActions([]);
      setSelectedAction('');
    }
  }, [editingTenancy.status, editingTenancy.id]);

  if (!show || !selectedListing) return null;

  // Check if we have any tenancies
  const hasTenancies = tenanciesList.length > 0;
  const createTenancyCheck = canCreateNewTenancy();

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center space-x-3">
                      <DocumentTextIcon className="w-6 h-6" />
                      <div className="text-left">
                        <Dialog.Title as="h2" className="text-lg font-semibold">
                          Tenancy Management
                        </Dialog.Title>
                        <p className="text-blue-100 text-sm">
                          {selectedListing?.room ? `Room ${selectedListing.room.room_number}` : selectedListing?.title || 'Listing'} - {selectedListing?.property?.name || 'Property'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-0">
                  {/* Tenancies List Display */}
                  <div className="bg-gray-50 border-b border-gray-200 p-6">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          <DocumentTextIcon className="w-5 h-5 text-gray-500 mr-2" />
                          Tenancies ({tenanciesList.length})
                        </h3>
                      </div>
                      <div className="relative">
                        <button
                          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            createTenancyCheck.canCreate
                              ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                          onClick={handleNewTenancy}
                          disabled={!createTenancyCheck.canCreate}
                          title={createTenancyCheck.canCreate ? '' : createTenancyCheck.reason}
                        >
                          <PlusIcon className="w-4 h-4 mr-2" />
                          Create Tenancy
                        </button>
                      </div>
                    </div>

                    {/* Tenancies List */}
                    {hasTenancies ? (
                      <div className="space-y-4">
                        {tenanciesList.map((tenancy, index) => (
                          <div
                            key={tenancy.id}
                            className={`bg-white rounded-xl shadow-sm border-2 transition-all duration-200 cursor-pointer hover:shadow-md ${
                              editingTenancy?.id === tenancy?.id 
                                ? 'border-blue-300 bg-blue-50' 
                                : tenancy.isNew 
                                  ? 'border-amber-300' 
                                  : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handleTenancySelection(tenancy)}
                          >
                            <div className="p-6">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  {tenancy.isNew ? (
                                    <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                                      <span className="inline-block w-2 h-2 bg-amber-400 rounded-full mr-2"></span>
                                      New Tenancy (Draft)
                                    </h4>
                                  ) : (
                                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                      {tenancy.rental_agreement?.start_date ?
                                        formatDateWithContext(tenancy.rental_agreement.start_date) : 'N/A'} - {' '}
                                      {tenancy.rental_agreement?.end_date ?
                                        formatDateWithContext(tenancy.rental_agreement.end_date) : 'N/A'}
                                    </h4>
                                  )}
                                  <div className="text-gray-600 mb-2 flex items-center">
                                    <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                                    {currencySymbol}{tenancy.rental_agreement?.amount?.amount ?
                                      (tenancy.rental_agreement.amount.amount / 100).toFixed(2) :
                                      (tenancy.rental_agreement?.amount || 0).toFixed(2)}
                                    {' '}{tenancy.rental_agreement?.rent_frequency || 'monthly'}
                                    {' · '}
                                    {tenancy.rental_agreement?.payment_type === 'postpay' ? 'Postpay' : 'Prepay'}
                                  </div>
                                  <div className="text-gray-600 flex items-center">
                                    <UserGroupIcon className="w-4 h-4 mr-1" />
                                    Tenants: {tenancy.rental_agreement?.renters && tenancy.rental_agreement.renters.length > 0
                                      ? tenancy.rental_agreement.renters.map(r => `${r.person?.given || r.given || r.first_name || ''} ${r.person?.surname || r.surname || r.last_name || ''}`.trim()).join(', ')
                                      : tenancy.isNew ? 'To be assigned' : 'No tenants assigned'
                                    }
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                                    tenancy.status === 'active' ? 'bg-green-100 text-green-800' : 
                                    tenancy.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                                    tenancy.status === 'terminated' ? 'bg-red-100 text-red-800' :
                                    tenancy.status === 'expired' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {tenancy.status || 'Unknown'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-lg font-medium text-gray-900">No Tenancies</h3>
                        <p className="mt-1 text-sm text-gray-500">Click "Create Tenancy" to set up a new tenancy for this listing.</p>
                      </div>
                    )}
                  </div>
            </div>

            {/* Tenancy Details Form */}
            {(editingTenancy.id || isCreatingNew) && (
              <div className="p-4">
                {/* Actions dropdown and Edit button */}
                {(editingTenancy.id && !isCreatingNew && !editingTenancy.isNew && (availableActions.length > 0 || true)) && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 mb-6">
                    <div className="flex items-center gap-4">
                      {availableActions.length > 0 && (
                        <>
                          <label className="text-sm font-semibold text-gray-700">Actions:</label>
                          <select
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-48"
                            value={selectedAction}
                            onChange={(e) => setSelectedAction(e.target.value)}
                          >
                            <option value="">Select an action...</option>
                            {availableActions.map(action => (
                              <option key={action.value} value={action.value}>
                                {action.label}
                              </option>
                            ))}
                          </select>
                          <button
                            className="flex items-center px-3 py-2 border border-blue-300 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleActionExecute}
                            disabled={!selectedAction}
                          >
                            <span className="mr-1">▶</span>
                            Execute Action
                          </button>
                        </>
                      )}
                    </div>
                    <div>
                      <button
                        className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                          isEditMode
                            ? hasUnsavedChanges
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-gray-500 hover:bg-gray-600 text-white'
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                        onClick={handleEditToggle}
                      >
                        {isEditMode ? (
                          hasUnsavedChanges ? (
                            <>
                              <CheckIcon className="w-4 h-4 mr-1" />
                              Save
                            </>
                          ) : (
                            <>
                              <XMarkIcon className="w-4 h-4 mr-1" />
                              Close
                            </>
                          )
                        ) : (
                          <>
                            <PencilIcon className="w-4 h-4 mr-1" />
                            Edit
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-blue-600 flex items-center">
                    <PencilIcon className="w-5 h-5 mr-2" />
                    {isCreatingNew || editingTenancy.isNew ? 'New Tenancy Details' : 'Tenancy Details'}
                  </h3>
                </div>

                <div className="row g-4">
                  {/* Occupant Search */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Occupants</label>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        value={occupantSearch}
                        onChange={handleOccupantSearchChange}
                        onFocus={() => setShowOccupantDropdown(true)}
                        onBlur={() => setTimeout(() => setShowOccupantDropdown(false), 200)}
                        placeholder="Search and add occupants..."
                        disabled={!isEditMode && !isCreatingNew && !editingTenancy.isNew}
                      />
                      {showOccupantDropdown && filteredOccupants.length > 0 && (isEditMode || isCreatingNew || editingTenancy.isNew) && (
                        <div className="dropdown-menu show position-absolute w-100" style={{ zIndex: 1060 }}>
                          {filteredOccupants.slice(0, 5).map(occupant => (
                            <button
                              key={occupant.id}
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => handleOccupantSelect(occupant)}
                              type="button"
                            >
                              <div>
                                <div className="fw-medium">{occupant.person?.given || occupant.given || occupant.first_name || ''} {occupant.person?.surname || occupant.surname || occupant.last_name || ''}</div>
                                {(occupant.person?.email || occupant.email) && <small className="text-muted">{occupant.person?.email || occupant.email}</small>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Display selected occupants as chips/badges */}
                    {selectedOccupants.length > 0 && (
                      <div className="mt-2">
                        {selectedOccupants.map(occupant => (
                          <div key={occupant.id} className="d-inline-flex align-items-center bg-light rounded p-2 me-2 mb-2" style={{ fontSize: '0.9rem' }}>
                            <i className="bi bi-person-check text-success me-1"></i>
                            <span className="me-2">
                              {occupant.person?.given || occupant.given || occupant.first_name || ''} {occupant.person?.surname || occupant.surname || occupant.last_name || ''}
                              {(occupant.person?.mobile || occupant.mobile || occupant.phone) && <span className="text-muted ms-1">• {occupant.person?.mobile || occupant.mobile || occupant.phone}</span>}
                            </span>
                            {(isEditMode || isCreatingNew || editingTenancy.isNew) && (
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-danger p-0"
                                onClick={() => handleRemoveOccupant(occupant.id)}
                                title="Remove occupant"
                                style={{ fontSize: '0.9rem', lineHeight: 1 }}
                              >
                                <i className="bi bi-x-circle"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedOccupants.length === 0 && (
                      <small className="text-muted mt-1 d-block">No occupants selected</small>
                    )}
                  </div>

                  {/* Status - Read Only */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Status</label>
                    <div className="form-control d-flex align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
                      <span className={`badge ${
                        editingTenancy.status === 'active' ? 'bg-success' : 
                        editingTenancy.status === 'pending' ? 'bg-primary' :
                        editingTenancy.status === 'terminated' ? 'bg-danger' :
                        editingTenancy.status === 'expired' ? 'bg-warning' :
                        'bg-secondary'
                      } me-2`}>
                        {editingTenancy.status || 'pending'}
                      </span>
                      <small className="text-muted">Status is managed automatically</small>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Start Date *</label>
                    {(!isEditMode && !isCreatingNew && !editingTenancy.isNew) ? (
                      <div className="form-control bg-light">
                        {editingTenancy.rental_agreement?.start_date
                          ? formatDateWithContext(editingTenancy.rental_agreement.start_date)
                          : '-'}
                      </div>
                    ) : (
                      <>
                        <input
                          type="date"
                          className="form-control"
                          value={editingTenancy.rental_agreement?.start_date || ''}
                          onChange={(e) => setEditingTenancy({
                            ...editingTenancy,
                            rental_agreement: {
                              ...editingTenancy.rental_agreement,
                              start_date: e.target.value
                            }
                          })}
                          onKeyDown={(e) => e.preventDefault()}
                          required
                        />
                        {editingTenancy.rental_agreement?.start_date && (
                          <small className="form-text text-primary fw-semibold">
                            {formatDateWithContext(editingTenancy.rental_agreement.start_date)}
                          </small>
                        )}
                      </>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">End Date *</label>
                    {(!isEditMode && !isCreatingNew && !editingTenancy.isNew) ? (
                      <div className="form-control bg-light">
                        {editingTenancy.rental_agreement?.end_date
                          ? formatDateWithContext(editingTenancy.rental_agreement.end_date)
                          : '-'}
                      </div>
                    ) : (
                      <>
                        <input
                          type="date"
                          className="form-control"
                          value={editingTenancy.rental_agreement?.end_date || ''}
                          onChange={(e) => setEditingTenancy({
                            ...editingTenancy,
                            rental_agreement: {
                              ...editingTenancy.rental_agreement,
                              end_date: e.target.value
                            }
                          })}
                          onKeyDown={(e) => e.preventDefault()}
                          required
                        />
                        {editingTenancy.rental_agreement?.end_date && (
                          <small className="form-text text-primary fw-semibold">
                            {formatDateWithContext(editingTenancy.rental_agreement.end_date)}
                          </small>
                        )}
                      </>
                    )}
                  </div>

                  {/* Rent Amount and Frequency on the same row */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Rent Amount ({agencyCurrency}) *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editingTenancy.rental_agreement?.amount || ''}
                      onChange={(e) => setEditingTenancy({
                        ...editingTenancy,
                        rental_agreement: {
                          ...editingTenancy.rental_agreement,
                          amount: Number(e.target.value)
                        }
                      })}
                      disabled={!isEditMode && !isCreatingNew && !editingTenancy.isNew}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Frequency *</label>
                    <select
                      className="form-select"
                      value={editingTenancy.rental_agreement?.rent_frequency || 'monthly'}
                      onChange={(e) => setEditingTenancy({
                        ...editingTenancy,
                        rental_agreement: {
                          ...editingTenancy.rental_agreement,
                          rent_frequency: e.target.value
                        }
                      })}
                      disabled={!isEditMode && !isCreatingNew && !editingTenancy.isNew}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Payment Type *</label>
                    <select
                      className="form-select"
                      value={editingTenancy.rental_agreement?.payment_type || 'prepay'}
                      onChange={(e) => setEditingTenancy({
                        ...editingTenancy,
                        rental_agreement: {
                          ...editingTenancy.rental_agreement,
                          payment_type: e.target.value
                        }
                      })}
                      disabled={!isEditMode && !isCreatingNew && !editingTenancy.isNew}
                    >
                      <option value="prepay">Prepay (pay in advance)</option>
                      <option value="postpay">Postpay (pay in arrears)</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Bond Amount ({agencyCurrency})</label>
                    {(!isEditMode && !isCreatingNew && !editingTenancy.isNew) ? (
                      // Read-only mode - display formatted value
                      <div className="form-control" style={{ backgroundColor: '#f8f9fa' }}>
                        {editingTenancy.rental_agreement?.bond_formatted ||
                         editingTenancy.original_bond_formatted ||
                         (editingTenancy.rental_agreement?.bond ? `${currencySymbol}${Number(editingTenancy.rental_agreement.bond).toFixed(2)}` : `${currencySymbol}0.00`)}
                      </div>
                    ) : (
                      // Edit mode - numeric input
                      <input
                        type="number"
                        className="form-control"
                        value={editingTenancy.rental_agreement?.bond || 0}
                        onChange={(e) => setEditingTenancy({
                          ...editingTenancy,
                          rental_agreement: {
                            ...editingTenancy.rental_agreement,
                            bond: Number(e.target.value),
                          }
                        })}
                        placeholder="0.00"
                      />
                    )}
                    <small className="text-muted">
                      Initial bond required for this tenancy
                    </small>
                  </div>

                  {/* Security Bond/Deposit Section - Only show for existing tenancies */}
                  {!isCreatingNew && !editingTenancy.isNew && (
                    <div className="col-12">
                      <label className="form-label fw-semibold">Security Bond/Deposit</label>
                      <div>
                        <small className="text-muted">
                          Current Balance: {loadingBondBalance ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          ) : (
                            <span className="fw-bold">{currencySymbol}{(typeof bondBalance === 'number' ? bondBalance : 0).toFixed(2)}</span>
                          )}
                        </small>
                        {isEditMode && editingTenancy.id && selectedOccupants.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm ms-3"
                            onClick={handleManageBond}
                          >
                            <i className="bi bi-plus-circle me-1"></i>
                            Manage Bond
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rental Documents Section - Only show for saved rental agreements */}
                  {!isCreatingNew && !editingTenancy.isNew && editingTenancy.rental_agreement?.id && (
                    <div className="col-12 mt-4">
                      <div className="border rounded p-3 bg-light">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="form-label fw-semibold mb-0">
                            <DocumentIcon className="w-5 h-5 inline-block me-2" />
                            Rental Documents
                          </label>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => setShowDocumentUpload(!showDocumentUpload)}
                            disabled={uploadingDocument}
                          >
                            <ArrowUpTrayIcon className="w-4 h-4 inline-block me-1" />
                            {showDocumentUpload ? 'Cancel' : 'Upload Document'}
                          </button>
                        </div>

                        {/* Upload Section */}
                        {showDocumentUpload && (
                          <div className="mb-3 p-3 border rounded bg-white">
                            <input
                              type="file"
                              ref={documentInputRef}
                              className="form-control"
                              onChange={handleDocumentUpload}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              multiple
                              disabled={uploadingDocument}
                            />
                            <small className="text-muted mt-1 d-block">
                              Accepted formats: PDF, DOC, DOCX, JPG, PNG
                            </small>
                            {uploadingDocument && (
                              <div className="mt-2">
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Uploading...
                              </div>
                            )}
                          </div>
                        )}

                        {/* Documents List */}
                        {loadingDocuments ? (
                          <div className="text-center py-3">
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Loading documents...
                          </div>
                        ) : rentalDocuments.length === 0 ? (
                          <div className="text-muted text-center py-3">
                            <DocumentIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="mb-0">No documents uploaded yet</p>
                          </div>
                        ) : (
                          <div className="list-group">
                            {rentalDocuments.map((doc) => (
                              <div key={doc.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div
                                  className="d-flex align-items-center"
                                  style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
                                  onClick={() => handleDownloadDocument(doc)}
                                  title={`Download ${doc.file_name}`}
                                >
                                  <DocumentIcon className="w-5 h-5 me-2 text-primary flex-shrink-0" />
                                  <div className="text-truncate">
                                    <span className="fw-medium text-primary">
                                      {doc.file_name}
                                    </span>
                                    <small className="text-muted d-block">
                                      Uploaded: {formatDateWithContext(doc.uploaded_at)}
                                      {doc.size && ` • ${(doc.size / 1024).toFixed(1)} KB`}
                                    </small>
                                  </div>
                                </div>
                                <div className="d-flex gap-2 ms-2 flex-shrink-0">
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleDownloadDocument(doc)}
                                    title="Download document"
                                  >
                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    title="Delete document"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Save/Cancel - Show for new tenancies */}
                  {(isCreatingNew || editingTenancy.isNew) && (
                    <div className="col-12 d-flex justify-content-end gap-2 mt-3">
                      <button className="btn btn-outline-secondary" onClick={handleCancel}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleSave}>Create Tenancy</button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-2xl">
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center"
                >
                  <XMarkIcon className="w-4 h-4 mr-2" />
                  Close
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>

    {/* Bond Modal */}
    <BondModal
      show={showBondModal}
      onClose={() => setShowBondModal(false)}
      tenancy={bondAgreement}
      selectedOccupant={bondOccupant}
      onSave={handleBondSave}
      onDataUpdate={async () => {
        // Refresh bond balance and notify parent for payment data refresh
        fetchBondBalance();
        if (onSave) {
          await onSave();
        }
      }}
    />
  </Dialog>
</Transition>
);
};

export default RentalAgreementModal;
